import { cn } from '@exam-prep/ui';
import { Menu, X } from 'lucide-react';
import type { RefObject } from 'react';
import { Brand, SidebarContent, type SidebarProps } from './Sidebar';

type NavProps = Omit<SidebarProps, 'navId' | 'collapsed' | 'onToggleCollapsed' | 'onNavigate'>;

/** Wide screens: the persistent sidebar, collapsible to an icon rail. */
export function DesktopSidebar({
  sidebar,
  navId,
  collapsed,
  toggleCollapsed,
}: {
  sidebar: NavProps;
  navId: string;
  collapsed: boolean;
  toggleCollapsed: () => void;
}) {
  return (
    <aside
      aria-label="Sidebar"
      className={cn(
        'fixed inset-y-0 left-0 z-30 hidden flex-col border-r bg-[var(--brand-surface-strong)] transition-[width] duration-200 motion-reduce:transition-none lg:flex',
        collapsed ? 'w-[76px]' : 'w-60',
      )}
    >
      <div
        className={cn('flex h-16 shrink-0 items-center px-4', collapsed && 'justify-center px-0')}
      >
        <Brand collapsed={collapsed} />
      </div>
      <SidebarContent
        {...sidebar}
        navId={`${navId}-side`}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
    </aside>
  );
}

/** Narrow screens: the top bar with the menu button and the page title. */
export function MobileTopBar({
  title,
  navId,
  open,
  onOpen,
  menuButtonRef,
}: {
  title: string;
  navId: string;
  open: boolean;
  onOpen: () => void;
  menuButtonRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-[color-mix(in_srgb,var(--brand-bg)_88%,transparent)] px-3 backdrop-blur-md lg:hidden">
      <button
        ref={menuButtonRef}
        type="button"
        aria-label="Open navigation"
        aria-expanded={open}
        aria-controls={`${navId}-drawer`}
        onClick={onOpen}
        className="flex size-11 items-center justify-center rounded-lg text-foreground outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Menu aria-hidden="true" className="size-5" />
      </button>
      <Brand />
      <span className="ml-auto truncate text-sm font-semibold">{title}</span>
    </header>
  );
}

/** Narrow screens: the off-canvas navigation drawer. */
export function NavDrawer({
  sidebar,
  navId,
  onClose,
  drawerRef,
}: {
  sidebar: NavProps;
  navId: string;
  onClose: () => void;
  drawerRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 size-full cursor-default bg-[color-mix(in_srgb,var(--brand-c-deep)_55%,transparent)] backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        ref={drawerRef}
        id={`${navId}-drawer`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="brand-drawer absolute inset-y-0 left-0 flex w-[min(300px,86vw)] flex-col border-r bg-[var(--brand-surface-strong)] shadow-2xl"
      >
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-4">
          <Brand />
          <button
            type="button"
            data-autofocus
            aria-label="Close navigation"
            onClick={onClose}
            className="flex size-11 items-center justify-center rounded-lg text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <SidebarContent {...sidebar} navId={`${navId}-drawer`} onNavigate={onClose} />
      </div>
    </div>
  );
}
